import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useClient, usePortal, type RefDomainRow, type RefLoadCheck, type RefResolved, type RefUsage, type RefValueRow } from "@app/portal-sdk";
import { Alert, Box, Button, Chip, DataTable, EmptyState, List, ListItem, ListItemButton, ListItemText, PageHeader, SectionCard, Stack, TextField, Typography } from "@app/ui-kit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { LoadingScreen } from "../components/LoadingScreen";
import { errorMessage, useApiQuery } from "../lib/useApiQuery";
import { uploadClientFile } from "../lib/upload";

/**
 * The one Reference data screen, as a drill-down by subject (RDN-3, which
 * supersedes SET-5's master and detail: three navigation columns left the
 * value table about 440px). Three levels, each at the full width of the
 * settings content region, with breadcrumbs as the back navigation:
 * Reference data / a subject / a list. REF-15 built every action on a list,
 * its refusals, its impact sentence and its revert; SET-5 moved that table
 * once and RDN-3 gives it room and nothing else. Tier is a chip on every
 * list in the detail view, always (RDN decision 1); in the subject and list
 * views it is shown only where it distinguishes (RDN-2, computed from the
 * resolved register, never tabulated). What a client may do is a sentence
 * (SET-6). Search finds a value across every list at every level (SET-7).
 * Screen state lives in the URL: ?subject=<code> opens a subject,
 * ?domain=<name> opens a list and resolves its own subject, ?value=<code>
 * highlights a value. No list name, level, subject label or code is named
 * in this file.
 */

/** SET-6: what a client may do, by the register's level. The level name itself never reaches the screen except in a tooltip. */
const MAY: Record<string, string> = {
  label_only: "You can rename these and change their order.",
  curated: "You can rename these, change their order, and hide ones you do not use.",
  open: "You can rename these, change their order, hide ones you do not use, and add your own."
};
/** The kind of list a level makes, for the tooltip of a disabled action: "<List> is a fixed list (label_only). ..." */
const KIND: Record<string, string> = { label_only: "a fixed list", curated: "a list you can hide from but not add to", open: "an open list" };
const codeChip = { height: 18, fontSize: 10, fontFamily: "ui-monospace, Menlo, monospace", "& .MuiChip-label": { textTransform: "none", letterSpacing: 0 } } as const;
/** Standards run to hundreds of values (countries, time zones); they open on demand and filter by text. */
const FOLD_ABOVE = 24;

type Form = { code: string; label: string; description: string; sortOrder: string };
const emptyForm: Form = { code: "", label: "", description: "", sortOrder: "" };

/** A list with the tier it sits at and, for an application's own list, which application. */
type ListRow = RefDomainRow & { app: string | null };

/** SET-5: the tier as a chip on the list, with what it means in one sentence. */
export const TierChip = ({ d, appName }: { d: ListRow; appName: (slug: string) => string }) => {
  const tier = d.scope === "platform"
    ? { label: "Fixed", title: "A standard the portal ships. You can rename its values; nothing can be added or retired." }
    : d.scope === "app"
      ? { label: `${appName(d.app ?? d.owningApp ?? "")} only`, title: `Only ${appName(d.app ?? d.owningApp ?? "")} reads this list.` }
      : { label: "Shared", title: "This list is yours, and every application you use reads it. A rename here shows everywhere." };
  return <Tooltip title={tier.title}><Chip label={tier.label} size="small" variant="outlined" color={d.scope === "client" ? "secondary" : "default"} aria-label={`${tier.label}: ${tier.title}`} /></Tooltip>;
};

export const ReferenceData = () => {
  const { api } = usePortal();
  const { client, canManage } = useClient();
  const [params, setParams] = useSearchParams();
  const wanted = params.get("domain");
  const wantedValue = params.get("value");
  const base = `/clients/${client.id}/reference`;

  // The shared lists and standards without any application's shadow; then each entitled application's own lists.
  const shared = useApiQuery<RefResolved>(() => api.request<RefResolved>(`${base}?all=true`), [api, base]);
  const apps = useMemo(() => shared.data?.apps ?? [], [shared.data]);
  const appKey = apps.map((a) => a.appSlug).join(",");
  const own = useApiQuery<Record<string, RefDomainRow[]>>(async () => {
    const out: Record<string, RefDomainRow[]> = {};
    for (const a of apps) out[a.appSlug] = (await api.request<RefResolved>(`${base}?all=true&app=${a.appSlug}&tiers=app`)).domains;
    return out;
  }, [api, base, appKey]);
  const appName = (slug: string) => apps.find((a) => a.appSlug === slug)?.name ?? slug;

  // Every list the person can see, with its tier, grouped by subject in register order.
  const lists = useMemo<ListRow[]>(() => {
    const out: ListRow[] = (shared.data?.domains ?? []).map((d) => ({ ...d, app: null }));
    for (const a of apps) for (const d of own.data?.[a.appSlug] ?? []) out.push({ ...d, app: a.appSlug });
    return out;
  }, [shared.data, own.data, apps]);
  const subjects = useMemo(() => {
    const by = new Map<string, { code: string; label: string; sort: number; lists: ListRow[] }>();
    for (const d of lists) {
      const s = by.get(d.subject) ?? { code: d.subject, label: d.subjectLabel, sort: d.subjectSort, lists: [] };
      s.lists.push(d); by.set(d.subject, s);
    }
    return [...by.values()].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label)).map((s) => ({ ...s, lists: s.lists.sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)) }));
  }, [lists]);
  const selected = wanted ? lists.find((d) => d.domain === wanted) ?? null : null;
  // RDN-3: the level comes from the URL. ?domain= resolves its own subject; ?subject= opens a subject; neither is the root.
  const wantedSubject = params.get("subject");
  const subject = selected ? subjects.find((s) => s.code === selected.subject) ?? null : wantedSubject ? subjects.find((s) => s.code === wantedSubject) ?? null : null;
  const level: "root" | "subject" | "list" = wanted ? "list" : wantedSubject ? "subject" : "root";
  const go = (next: { subject?: string | null; domain?: string | null; value?: string | null }) => {
    const p = new URLSearchParams(params);
    p.delete("subject"); p.delete("domain"); p.delete("value");
    if (next.domain) { p.set("domain", next.domain); if (next.value) p.set("value", next.value); }
    else if (next.subject) p.set("subject", next.subject);
    setParams(p);
  };
  // RDN-2: a subject group is tier-uniform when every list in it sits at one tier; the tier then belongs to the
  // group header and its rows read the count alone. Computed from the register, so a group that becomes mixed
  // when an application is enabled starts showing per-row tiers with no code change.
  const tierKey = (d: ListRow) => (d.scope === "app" ? `app:${d.app ?? d.owningApp ?? ""}` : d.scope);
  const tierWord = (d: ListRow) => (d.scope === "platform" ? "fixed" : d.scope === "app" ? `${appName(d.app ?? d.owningApp ?? "")} only` : "shared");
  const tierTitle = (d: ListRow) => d.scope === "platform" ? "A standard the portal ships. You can rename its values; nothing can be added or retired." : d.scope === "app" ? `Only ${appName(d.app ?? d.owningApp ?? "")} reads this list.` : "This list is yours, and every application you use reads it. A rename here shows everywhere.";
  const uniformTier = (group: { lists: ListRow[] }): ListRow | null => { const keys = new Set(group.lists.map(tierKey)); return keys.size === 1 ? group.lists[0] : null; };

  // ---- SET-7: find a value, not just a list ---------------------------------------------------------
  // Client-side, over the register already loaded for the screen: about fourteen hundred values including the
  // standards, matched by a lower-cased substring, which is instant and needs no second endpoint.
  const [query, setQuery] = useState("");
  const previous = useRef<{ subject: string | null; domain: string | null } | null>(null);
  const term = query.trim().toLowerCase();
  const hits = useMemo(() => {
    if (!term) return [];
    const out: { list: ListRow; values: RefValueRow[] }[] = [];
    for (const d of lists) {
      const vs = d.values.filter((v) => v.label.toLowerCase().includes(term) || v.code.toLowerCase().includes(term) || (v.description ?? "").toLowerCase().includes(term) || (v.systemLabel ?? "").toLowerCase().includes(term));
      if (vs.length) out.push({ list: d, values: vs });
    }
    return out;
  }, [lists, term]);
  // Clearing the search returns to the selection the person had before typing, not to nothing.
  const startSearch = (next: string) => {
    if (!query && next) previous.current = { subject: wantedSubject, domain: wanted };
    setQuery(next);
    if (!next && previous.current) go({ subject: previous.current.subject, domain: previous.current.domain });
  };

  // ---- the value table's actions, exactly as REF-15 built them --------------------------------------
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ value: RefValueRow; app: string | null } | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [retiring, setRetiring] = useState<{ value: RefValueRow; app: string | null; usage: RefUsage } | null>(null);
  const [unfolded, setUnfolded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Record<string, string>>({});
  const after = async () => { await shared.reload(); await own.reload(); };
  const run = async (fn: () => Promise<void>) => { setBusy(true); setError(null); try { await fn(); await after(); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); } };
  const q = (app: string | null) => (app ? `?app=${app}` : "");
  const add = (domain: string, app: string | null) => run(async () => {
    await api.request(`${base}/values${q(app)}`, { method: "POST", json: { domain, code: form.code.trim().toLowerCase(), label: form.label.trim(), description: form.description.trim() || null } });
    setAdding(null);
  });
  const saveEdit = () => editing && run(async () => {
    await api.request(`${base}/values/${editing.value.id}${q(editing.app)}`, { method: "PATCH", json: { label: form.label.trim(), description: form.description.trim() || null, ...(form.sortOrder.trim() ? { sortOrder: Number(form.sortOrder) } : {}) } });
    setEditing(null);
  });
  const revert = (v: RefValueRow, app: string | null) => run(async () => { await api.request(`${base}/values/${v.id}${q(app)}`, { method: "PATCH", json: { revert: true } }); });
  const askRetire = (v: RefValueRow, app: string | null) => run(async () => { const usage = await api.request<RefUsage>(`${base}/values/${v.id}/usage${q(app)}`); setRetiring({ value: v, app, usage }); });
  const retire = () => retiring && run(async () => { await api.request(`${base}/values/${retiring.value.id}${q(retiring.app)}`, { method: "PATCH", json: { isActive: false } }); setRetiring(null); });
  const restore = (v: RefValueRow, app: string | null) => run(async () => { await api.request(`${base}/values/${v.id}${q(app)}`, { method: "PATCH", json: { isActive: true } }); });
  const openEdit = (v: RefValueRow, app: string | null) => { setEditing({ value: v, app }); setForm({ code: v.code, label: v.label, description: v.description ?? "", sortOrder: String(v.sortOrder) }); };

  // ---- REF-20 to REF-22: the workbook out and back in --------------------------------------------------
  const fileInput = useRef<HTMLInputElement>(null);
  const [check, setCheck] = useState<RefLoadCheck | null>(null);
  const [loadPhase, setLoadPhase] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [workbookOpen, setWorkbookOpen] = useState(false);
  const exportWorkbook = async (domain?: string) => { setLoadError(null); try { await api.download(`${base}/export${domain ? `?domain=${encodeURIComponent(domain)}` : ""}`); } catch (err) { setLoadError(errorMessage(err)); } };
  const checkWorkbook = async (file: File) => {
    setLoadError(null); setCheck(null); setLoadPhase("Sending the file…");
    try {
      const uploaded = await uploadClientFile(api, client.id, file, "reference", (step) => setLoadPhase(step === "upload" ? "Sending the file…" : "Preparing…"));
      setLoadPhase("Checking every row…");
      setCheck(await api.request<RefLoadCheck>(`${base}/loads`, { method: "POST", json: { fileId: uploaded.id } }));
    } catch (err) { setLoadError(errorMessage(err)); } finally { setLoadPhase(null); }
  };
  const commitWorkbook = async () => {
    if (!check) return;
    setLoadError(null); setLoadPhase("Loading…");
    try { setCheck(await api.request<RefLoadCheck>(`${base}/loads/${check.checkId}/commit`, { method: "POST" })); await after(); }
    catch (err) { setLoadError(errorMessage(err)); } finally { setLoadPhase(null); }
  };

  if ((shared.loading && !shared.data) || (own.loading && !own.data)) return <LoadingScreen />;
  if (shared.error) return <Alert severity="error">The reference lists could not be loaded. {shared.error}</Alert>;
  if (own.error) return <Alert severity="error">The application lists could not be loaded. {own.error}</Alert>;
  if (lists.length === 0) return <EmptyState eyebrow="Reference data" title="No lists yet" description={apps.length === 0 ? "Your lists are created when the first application is enabled for you." : "Your lists have not been set up yet. Saving an application's entitlement in the console creates them."} />;

  const canRetire = (d: ListRow) => d.editability !== "label_only";
  const canAdd = (d: ListRow) => d.editability === "open";
  const readers = (d: ListRow): string => {
    if (d.scope === "app") return `Used only by ${appName(d.app ?? "")}.`;
    const names = d.readBy.map((s) => appName(s));
    if (names.length === 0) return apps.length === 0 ? "No application is enabled for you yet." : "Not read by any of your applications.";
    if (names.length === 1) return `Used by ${names[0]}.`;
    return `Used by ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}.`;
  };
  // SET-6: a disabled action always says why, naming the list; the level name lives here and nowhere else on the screen.
  const addTooltip = (d: ListRow) => `${d.label} is ${KIND[d.editability] ?? "a fixed list"} (${d.editability}). You can rename these values but not add to them.${d.editabilityReason ? ` ${d.editabilityReason}` : ""}`;
  const retireTooltip = (d: ListRow, v: RefValueRow) => v.isProtected ? v.protectedReason ?? "" : canRetire(d) ? "" : `${d.label} is ${KIND[d.editability] ?? "a fixed list"} (${d.editability}). You can rename these values but not retire them.${d.editabilityReason ? ` ${d.editabilityReason}` : ""}`;

  // ---- the detail pane: one list, the table REF-15 built -----------------------------------------------
  const detail = (d: ListRow) => {
    const app = d.app;
    const key = `${app ?? "client"}:${d.domain}`;
    const folded = d.values.length > FOLD_ABOVE && !unfolded.has(d.domain) && !wantedValue;
    const text = (filter[key] ?? "").trim().toLowerCase();
    const values = text ? d.values.filter((v) => v.label.toLowerCase().includes(text) || v.code.toLowerCase().includes(text) || (v.systemLabel ?? "").toLowerCase().includes(text)) : d.values;
    return (
      <Stack spacing={2} key={key} id={`domain-${d.domain}`}>
        <div>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5">{d.label}</Typography>
            <TierChip d={d} appName={appName} />
            <Chip label={d.cardinality === "many" ? "Many per row" : "One per row"} size="small" variant="outlined" />
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" startIcon={<DownloadIcon />} onClick={() => void exportWorkbook(d.domain)} disabled={loadPhase !== null}>Download this list</Button>
          </Stack>
          {d.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{d.description}</Typography>}
        </div>
        <Typography variant="body2">{MAY[d.editability] ?? MAY.label_only}{d.editabilityReason ? ` ${d.editabilityReason}` : ""}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{readers(d)}</Typography>
        {folded ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">{d.values.length} values.</Typography>
            <Button size="small" onClick={() => setUnfolded((s) => new Set(s).add(d.domain))}>Show them</Button>
          </Stack>
        ) : (
          <>
            {d.values.length > FOLD_ABOVE && <TextField size="small" label="Find a value in this list" value={filter[key] ?? ""} onChange={(e) => setFilter((f) => ({ ...f, [key]: e.target.value }))} sx={{ maxWidth: 320 }} />}
            <Box sx={{ overflowX: "auto" }}>
              <DataTable
                columns={[{ key: "label", label: "Value" }, { key: "code", label: "Code" }, { key: "description", label: "Description" }, { key: "order", label: "Order" }, { key: "origin", label: "Origin" }, { key: "state", label: "State" }, { key: "actions", label: "", sortable: false }]}
                rows={values.map((v) => ({
                  label: (
                    <Stack spacing={0} sx={v.code === wantedValue ? { outline: "2px solid", outlineColor: "secondary.main", borderRadius: 1, px: 0.5, mx: -0.5 } : undefined} id={v.code === wantedValue ? "value-highlight" : undefined}>
                      <Typography variant="body2" color={v.isActive ? "text.primary" : "text.disabled"} sx={{ fontWeight: 500 }}>{v.label}</Typography>
                      {v.renamed && (
                        <Typography variant="caption" color="text.secondary">
                          standard word <em>{v.systemLabel}</em>
                          {canManage && <> · <Link component="button" underline="hover" variant="caption" disabled={busy} onClick={() => void revert(v, app)}>Revert</Link></>}
                        </Typography>
                      )}
                    </Stack>
                  ),
                  code: <Chip label={v.code} size="small" variant="outlined" sx={codeChip} />,
                  description: <Typography variant="body2" color={v.isActive ? "text.secondary" : "text.disabled"}>{v.description ?? ""}</Typography>,
                  order: v.sortOrder,
                  origin: v.systemLabel === null ? "Yours" : v.renamed ? "Standard, relabeled" : "Standard",
                  state: (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{v.isActive ? "In use" : "Retired"}</span>
                      {v.renamed && <Chip label="renamed" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      {v.isProtected && <Tooltip title={v.protectedReason ?? ""}><Chip label="protected" size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} /></Tooltip>}
                    </Stack>
                  ),
                  actions: canManage ? (
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" disabled={busy} onClick={() => openEdit(v, app)}>{v.systemLabel === null ? "Edit" : "Relabel"}</Button>
                      {v.isActive
                        ? <Tooltip title={retireTooltip(d, v)}><span><Button size="small" color="error" disabled={busy || v.isProtected || !canRetire(d)} onClick={() => void askRetire(v, app)}>Retire</Button></span></Tooltip>
                        : <Tooltip title={canRetire(d) ? "" : retireTooltip(d, v)}><span><Button size="small" disabled={busy || !canRetire(d)} onClick={() => void restore(v, app)}>Restore</Button></span></Tooltip>}
                    </Stack>
                  ) : ""
                }))}
              />
            </Box>
            {values.length === 0 && <Typography variant="body2" color="text.secondary">{d.values.length === 0 ? "No values in this list yet." : "No value matches."}</Typography>}
          </>
        )}
        {canManage && (canAdd(d) ? (adding === key ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 2fr auto auto" }, gap: 1, alignItems: "start" }}>
            <TextField size="small" label="Code" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} helperText="Lower-case letters, digits, underscores" autoFocus />
            <TextField size="small" label="Value" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
            <TextField size="small" label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} helperText="One line: what the tooltip says." />
            <Button variant="contained" size="small" disabled={busy || !form.code.trim() || !form.label.trim()} onClick={() => void add(d.domain, app)}>Add</Button>
            <Button size="small" onClick={() => setAdding(null)}>Cancel</Button>
          </Box>
        ) : (
          <Stack direction="row"><Button size="small" onClick={() => { setAdding(key); setForm(emptyForm); }}>Add a value</Button></Stack>
        )) : (
          <Tooltip title={addTooltip(d)}><span><Button size="small" disabled>Add a value</Button></span></Tooltip>
        ))}
        {neighbours && (neighbours.prev || neighbours.next) && (
          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ pt: 1, borderTop: 1, borderColor: "divider" }} component="nav" aria-label="Neighbouring lists">
            {neighbours.prev ? <Button size="small" startIcon={<ChevronLeftIcon />} onClick={() => go({ domain: neighbours.prev!.domain })}>{neighbours.prev.label}</Button> : <span />}
            {neighbours.next ? <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => go({ domain: neighbours.next!.domain })}>{neighbours.next.label}</Button> : <span />}
          </Stack>
        )}
      </Stack>
    );
  };
  const neighbours = selected && subject ? (() => { const i = subject.lists.findIndex((l) => l.domain === selected.domain && l.app === selected.app); return { prev: i > 0 ? subject.lists[i - 1] : null, next: i >= 0 && i < subject.lists.length - 1 ? subject.lists[i + 1] : null }; })() : null;

  // ---- search, at every level ----------------------------------------------------------------------------
  const search = (
    <Stack spacing={1.5}>
      <TextField size="small" label="Find a value" placeholder="A word, a code or a description" value={query} onChange={(e) => startSearch(e.target.value)} sx={{ maxWidth: 480 }} fullWidth inputProps={{ "aria-label": "Find a value across every list" }} />
      {term && (
        hits.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nothing matches "{query.trim()}" in the lists you can see.</Typography>
        ) : (
          <List dense disablePadding aria-label="Search results" sx={{ maxWidth: 640 }}>
            {hits.map(({ list, values }) => (
              <li key={`${list.app ?? "client"}:${list.domain}`} style={{ listStyle: "none" }}>
                <Typography variant="overline" sx={{ color: "secondary.main", display: "block", px: 1, pt: 1 }}>{list.subjectLabel} · {list.label}</Typography>
                <List dense disablePadding>
                  {values.slice(0, 8).map((v) => (
                    <ListItem key={v.code} disablePadding>
                      <ListItemButton selected={wanted === list.domain && wantedValue === v.code} onClick={() => { setQuery(""); go({ domain: list.domain, value: v.code }); }}>
                        <ListItemText primary={v.label} secondary={`${v.code}${v.renamed && v.systemLabel ? ` · standard word ${v.systemLabel}` : ""}${v.isActive ? "" : " · retired"}`} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                {values.length > 8 && <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>and {values.length - 8} more in {list.label}</Typography>}
              </li>
            ))}
          </List>
        )
      )}
    </Stack>
  );

  // ---- level 1: the subjects; level 2: a subject's lists ---------------------------------------------------
  const subjectRows = (
    <List disablePadding aria-label="Subjects" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
      {subjects.map((s) => {
        const uniform = uniformTier(s);
        return (
          <ListItem key={s.code} disablePadding sx={{ border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
            <ListItemButton onClick={() => go({ subject: s.code })} sx={{ borderRadius: 1, py: 1.5 }}>
              <ListItemText
                primary={<Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{s.label}</Typography>{uniform && <Tooltip title={tierTitle(uniform)}><Typography variant="body2" color="text.secondary" component="span">· {tierWord(uniform)}</Typography></Tooltip>}</Stack>}
                secondary={`${s.lists.length} list${s.lists.length === 1 ? "" : "s"}`}
              />
              <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
  const listRows = (s: NonNullable<typeof subject>) => {
    const uniform = uniformTier(s);
    return (
      <List disablePadding aria-label={`Lists in ${s.label}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
        {s.lists.map((d) => {
          const active = d.values.filter((v) => v.isActive).length;
          return (
            <ListItem key={`${d.app ?? "client"}:${d.domain}`} disablePadding sx={{ border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
              <ListItemButton onClick={() => go({ domain: d.domain })} sx={{ borderRadius: 1, py: 1.5 }}>
                <ListItemText
                  primary={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{d.label}</Typography>}
                  secondary={uniform ? `${active} value${active === 1 ? "" : "s"}` : <Tooltip title={tierTitle(d)}><span>{active} value{active === 1 ? "" : "s"} · {tierWord(d)}</span></Tooltip>}
                />
                <ChevronRightIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  // ---- the breadcrumb: the back navigation, and it says where you are ------------------------------------
  const crumbs = (
    <Breadcrumbs aria-label="Breadcrumb" component="nav">
      {level === "root" ? <Typography color="text.primary" aria-current="page">Reference data</Typography> : <Link component="button" underline="hover" color="inherit" onClick={() => go({})}>Reference data</Link>}
      {level !== "root" && subject && (level === "subject" ? <Typography color="text.primary" aria-current="page">{subject.label}{uniformTier(subject) ? ` · ${tierWord(uniformTier(subject)!)}` : ""}</Typography> : <Link component="button" underline="hover" color="inherit" onClick={() => go({ subject: subject.code })}>{subject.label}</Link>)}
      {level === "list" && selected && <Typography color="text.primary" aria-current="page">{selected.label}</Typography>}
      {level === "list" && !selected && <Typography color="text.primary" aria-current="page">Not found</Typography>}
      {level === "subject" && !subject && <Typography color="text.primary" aria-current="page">Not found</Typography>}
    </Breadcrumbs>
  );

  const workbook = (
    <SectionCard eyebrow="The workbook" title="Every list, out and back in">
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" size="small" onClick={() => void exportWorkbook()} disabled={loadPhase !== null}>Export every list</Button>
          {canManage && (
            <>
              <input ref={fileInput} type="file" accept=".xlsx" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void checkWorkbook(f); }} />
              <Button variant="contained" size="small" onClick={() => fileInput.current?.click()} disabled={loadPhase !== null}>Upload and check</Button>
            </>
          )}
          <Button size="small" onClick={() => setWorkbookOpen((o) => !o)} aria-expanded={workbookOpen}>{workbookOpen ? "Fewer words" : "How it works"}</Button>
          {loadPhase && <Typography variant="body2" color="text.secondary">{loadPhase}</Typography>}
        </Stack>
        <Collapse in={workbookOpen}>
          <Typography variant="body2" color="text.secondary">Export the whole vocabulary as a workbook: one sheet each for shared lists, application lists and standards, with a README. Rename values, add codes to open lists, mark a row retire or restore, and upload it. The check reports every row and says in one sentence what the file would retire across your applications; nothing is written until you commit, and a file with a rejected row is not committed at all. Deleting a row does nothing.</Typography>
        </Collapse>
        {loadError && <Alert severity="error" onClose={() => setLoadError(null)}>{loadError}</Alert>}
        {check && (
          <Stack spacing={1.5} sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}>
            <Typography variant="subtitle2">{check.committedAt ? `Loaded ${check.fileName}` : `Check of ${check.fileName}`}</Typography>
            <Typography variant="body2">{check.counts.add} to add, {check.counts.relabel} to relabel, {check.counts.retire} to retire, {check.counts.restore} to restore, {check.counts.unchanged} unchanged, {check.counts.rejected} rejected.</Typography>
            <Alert severity={check.impact.retires === 0 ? "success" : check.impact.rowsTouched > 0 ? "warning" : "info"} icon={false}>{check.impact.sentence}</Alert>
            {check.rows.some((r) => r.outcome !== "unchanged") && (
              <Box sx={{ overflowX: "auto" }}>
                <DataTable
                  sortable={false}
                  columns={[{ key: "sheet", label: "Sheet" }, { key: "row", label: "Row" }, { key: "domain", label: "List" }, { key: "code", label: "Code" }, { key: "label", label: "Label" }, { key: "outcome", label: "Outcome" }, { key: "reason", label: "Reason" }]}
                  rows={check.rows.filter((r) => r.outcome !== "unchanged").map((r) => ({
                    sheet: r.sheet, row: r.row, domain: r.domain, code: <Chip label={r.code} size="small" variant="outlined" sx={codeChip} />, label: r.label ?? "",
                    outcome: <Chip label={r.outcome} size="small" color={r.outcome === "rejected" ? "error" : r.outcome === "retire" ? "warning" : "default"} variant="outlined" sx={{ height: 20, fontSize: 11 }} />,
                    reason: <Typography variant="body2" color="text.secondary">{r.reason ?? ""}</Typography>
                  }))}
                />
              </Box>
            )}
            {!check.committedAt && canManage && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button variant="contained" size="small" color={check.impact.rowsTouched > 0 ? "warning" : "primary"} onClick={() => void commitWorkbook()} disabled={!check.canCommit || loadPhase !== null}>{check.impact.retires > 0 ? "Commit, retiring as stated" : "Commit"}</Button>
                {!check.canCommit && <Typography variant="body2" color="text.secondary">Fix the rejected rows in the file and check it again; nothing is loaded while any row is rejected.</Typography>}
                {check.canCommit && check.counts.add + check.counts.relabel + check.counts.retire + check.counts.restore === 0 && <Typography variant="body2" color="text.secondary">The file changes nothing.</Typography>}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );

  return (
    <Stack spacing={3}>
      <PageHeader eyebrow={client.name} title="Reference data" subtitle={level === "root" ? `The lists behind every dropdown in every application: ${lists.length} lists across ${subjects.length} subjects. Find a list by what it is about; the tier says whose it is.` : level === "subject" && subject ? `${subject.lists.length} list${subject.lists.length === 1 ? "" : "s"} in ${subject.label}.` : selected ? `${selected.subjectLabel}.` : "The link may be out of date."} />
      {crumbs}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {!canManage && level === "root" && <Alert severity="info">You can read every list here. A client administrator edits them.</Alert>}
      {search}
      {!term && level === "root" && (
        <>
          {workbook}
          {subjectRows}
        </>
      )}
      {!term && level === "subject" && (subject ? (subject.lists.length === 0 ? <EmptyState eyebrow="Reference data" title="No lists in this subject" description="Nothing you can see is filed here yet." /> : listRows(subject)) : <EmptyState eyebrow="Reference data" title="There is no subject by that name" description="The link may be out of date." />)}
      {!term && level === "list" && (selected ? detail(selected) : <EmptyState eyebrow="Reference data" title="There is no list by that name" description="The link may be out of date, or the list belongs to an application you are not entitled to." />)}

      <Dialog open={editing !== null} onClose={() => !busy && setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.value.systemLabel === null ? `Edit ${editing?.value.label}` : `Relabel ${editing?.value.systemLabel}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label="Your word for it" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} autoFocus helperText={editing && editing.value.systemLabel !== null ? `The standard word is "${editing.value.systemLabel}". It stays beside yours and you can revert to it.` : undefined} />
            <TextField size="small" label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} multiline minRows={2} />
            <TextField size="small" label="Order" type="number" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} sx={{ maxWidth: 160 }} helperText="Lower sorts first." />
            <Typography variant="caption" color="text.secondary">The code {editing?.value.code} stays as it is; rows already using it keep it.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveEdit()} disabled={busy || !form.label.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={retiring !== null} onClose={() => !busy && setRetiring(null)} fullWidth maxWidth="xs">
        <DialogTitle>Retire {retiring?.value.label}?</DialogTitle>
        <DialogContent><Typography variant="body2">{retiring?.usage.sentence}</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRetiring(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void retire()} disabled={busy || retiring?.usage.canRetire === false}>Retire</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
